/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {
    Box,
    Button,
    Container,
    Flex,
    Heading,
    SimpleGrid,
    Stack,
    useMultiStyleConfig,

    // Hooks
    useTheme,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
// import Link from '@salesforce/retail-react-app/app/components/link'

// Project Components
import LinksList from './links-list'
// Others
import {categoryUrlBuilder} from '../../utills/url'
import {ChevronRightIcon, ChevronLeftIcon} from '../icons'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'

const ListMenuContent = ({
    item,
    itemsKey,
    onClose,
    initialFocusRef,
    setfeatureImage,
    setSelectedLevelOneMenu,
    setLinksForLevelThreeMenu,
    setSelectedLevelTwoMenu,
    selectedLevelOneMenu,
    selectedLevelTwoMenu,
    setLinksForLevelTwoMenu,
    setIsLevelThreeMenuOpen,
    setIsLevelTwoMenuOpen,
    isLevelTwoMenuOpen,
    isLevelThreeMenuOpen,
    isMobileMenu = false,
    setLinksForContentComp,
    setFeatureImageMobileLevelTwo,
    setFeatureImageMobileLevelThree,
    setFeatureImageMobileLevelFour,
    setMenuToggle
}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const {locale} = useIntl()
    const intl = useIntl()
    const items = item?.[itemsKey] || []

    const variant = 'vertical'

    const styles = useMultiStyleConfig('LinksList', {variant})

    const handleCloseMainContent = () => {
        setLinksForContentComp([])
    }

    const updateGTMdataLayer = (categoryName) => {
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: categoryName,
                valueL2: `view all ${categoryName}`
            })
        }
    }
    const viewAllHref = categoryUrlBuilder(item, locale)
    const parentCategoryName = item.name
    return (
        <>
            <Container
                as={Stack}
                {...baseStyle.popoverContainer}
                {...baseStyle.overflow}
                position={'relative'}
                marginTop={{base: pxToRem(50), sm: pxToRem(50), md: 0, lg: 0}}
            >
                <Flex
                    direction="column"
                    flex={'1'}
                    borderRight={!isMobileMenu ? `${pxToRem(1)} solid black` : '0 solid black'}
                >
                    {isMobileMenu && (
                        <>
                            <Box {...styles.back}>
                                <ChevronLeftIcon
                                    fontFamily={fonts.primary}
                                    fontWeight={'700'}
                                    marginLeft={pxToRem(-10)}
                                />
                                <Button
                                    onClick={handleCloseMainContent}
                                    _hover={{
                                        textDecoration: 'none',
                                        color: 'black'
                                    }}
                                    _active={{
                                        backgroundColor: 'transparent'
                                    }}
                                    backgroundColor={'transparent'}
                                    height={'auto'}
                                    paddingTop={pxToRem(10)}
                                    paddingLeft={'0'}
                                    paddingBottom={pxToRem(10)}
                                    color={'black'}
                                    display={'block'}
                                    width={'10px'}
                                    fontSize={pxToRem(14)}
                                    fontFamily={fonts.primary}
                                    fontWeight={'700'}
                                    justifyContent={'flex-end'}
                                    letterSpacing={pxToRem(1)}
                                >
                                    {intl.formatMessage({
                                        id: 'header.link.back',
                                        defaultMessage: 'BACK'
                                    })}
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* view all */}
                    <Link
                        // to={viewAllHref}
                        href={viewAllHref}
                        // onClick={onLinkClick}
                        // onClick={() => onLevelOneClick(heading.id)}
                        // ref={headingLinkRef}
                        onClick={() => updateGTMdataLayer(item.name)}
                        {...styles.headingLink}
                        _hover={{
                            textDecoration: 'none'
                        }}
                        paddingTop={{
                            base: pxToRem(10),
                            sm: pxToRem(10),
                            md: pxToRem(0),
                            lg: pxToRem(0)
                        }}
                        paddingBottom={{
                            base: pxToRem(10),
                            sm: pxToRem(10),
                            md: pxToRem(0),
                            lg: pxToRem(0)
                        }}
                        paddingLeft={'0'}
                        paddingRight={{
                            base: pxToRem(18),
                            sm: pxToRem(18),
                            md: pxToRem(15),
                            lg: pxToRem(15)
                        }}
                    >
                        <Heading
                            {...styles.heading}
                            fontSize={[pxToRem(13), pxToRem(13), pxToRem(13), pxToRem(13)]}
                            fontWeight="400"
                            padding={[0, 0, 0, 0]}
                        >
                            view all {item.name}
                        </Heading>
                    </Link>

                    {/* level category menu items */}
                    {items.map((item, index) => {
                        const {id, name, c_headerMenuBanner} = item
                        const items = item[itemsKey]
                        const heading = {
                            id: id,
                            image: c_headerMenuBanner,
                            href: categoryUrlBuilder(item, locale),
                            text: name,
                            styles: {
                                fontSize: 'md',
                                marginBottom: 2
                            }
                        }

                        const links = items
                            ? items.map((item) => {
                                  const {name, id, categories} = item
                                  return {
                                      id: id,
                                      categories: categories,
                                      name: name,
                                      href: categoryUrlBuilder(item, locale),
                                      text: name,
                                      styles: {
                                          fontSize: 'md',
                                          paddingTop: 3,
                                          paddingBottom: 3
                                      }
                                  }
                              })
                            : []

                        return (
                            <LinksList
                                key={id}
                                categoryData={item}
                                hasSubMenu={item?.categories ? true : false}
                                isMobileMenu={isMobileMenu}
                                setMenuToggle={setMenuToggle}
                                heading={heading}
                                links={links}
                                color={'gray.900'}
                                onLinkClick={onClose}
                                {...(index === 0 ? {headingLinkRef: initialFocusRef} : {})}
                                setfeatureImage={setfeatureImage}
                                setSelectedLevelOneMenu={setSelectedLevelOneMenu}
                                setLinksForLevelThreeMenu={setLinksForLevelThreeMenu}
                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                selectedLevelOneMenu={selectedLevelOneMenu}
                                setSelectedLevelTwoMenu={setSelectedLevelTwoMenu}
                                setLinksForLevelTwoMenu={setLinksForLevelTwoMenu}
                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                setLinksForContentComp={setLinksForContentComp}
                                setFeatureImageMobileLevelTwo={setFeatureImageMobileLevelTwo}
                                setFeatureImageMobileLevelThree={setFeatureImageMobileLevelThree}
                                setFeatureImageMobileLevelFour={setFeatureImageMobileLevelFour}
                                parentCategoryName={parentCategoryName}
                            />
                        )
                    })}
                </Flex>
            </Container>
        </>
    )
}

ListMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    setfeatureImage: PropTypes.string,
    selectedLevelOneMenu: PropTypes.func,
    selectedLevelTwoMenu: PropTypes.func,
    setSelectedLevelTwoMenu: PropTypes.func,
    setLinksForLevelThreeMenu: PropTypes.func,
    setLinksForLevelTwoMenu: PropTypes.array,
    setIsLevelThreeMenuOpen: PropTypes.func,
    setIsLevelTwoMenuOpen: PropTypes.func,
    setLinksForContentComp: PropTypes.func,
    isMobileMenu: PropTypes.oneOf[(true, false)],
    setFeatureImageMobileLevelTwo: PropTypes.func,
    setFeatureImageMobileLevelThree: PropTypes.func,
    setFeatureImageMobileLevelFour: PropTypes.func,
    setMenuToggle: PropTypes.func,
    isLevelTwoMenuOpen: PropTypes.bool,
    isLevelThreeMenuOpen: PropTypes.bool,
    setSelectedLevelOneMenu: PropTypes.func
}

export {ListMenuContent}
